import * as Sentry from "@sentry/react";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@shared/components/ui/toaster";
import { Toaster as Sonner } from "@shared/components/ui/sonner";
import { TooltipProvider } from "@shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useActiveTheme } from "@shared/hooks/useActiveTheme";
import { usePresenceTracker } from "@shared/hooks/usePresence";
import { useFeedbackRequest } from "@shared/hooks/useFeedbackRequest";
import { usePageVisitTracker } from "@shared/hooks/usePageVisitTracker";
import { PlatformLogoProvider } from "@shared/hooks/usePlatformLogo";
import { useDynamicFavicon } from "@shared/hooks/useDynamicFavicon";
import { AdminImpersonationProvider } from "./providers/AdminImpersonationContext";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import MaintenanceMode from "@shared/components/layout/MaintenanceMode";
import { FeedbackRequestDialog } from "@shared/components/common/FeedbackRequestDialog";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@shared/utils/chunkErrorRecovery";

// Eager load the home page for fast first paint
import StartClinic from "@features/public/pages/StartClinic";

// Lazy load all other pages
const Programs = lazy(() => import("@features/public/pages/Programs"));
const Index = lazy(() => import("@features/meal-planner/pages/Index"));
const CalorieCalculator = lazy(() => import("@features/calculators/pages/CalorieCalculator"));
const DietitianCalculator = lazy(() => import("@features/calculators/pages/DietitianCalculator"));
const RenalCalculator = lazy(() => import("@features/calculators/pages/RenalCalculator"));
const FormulaCalculator = lazy(() => import("@features/calculators/pages/FormulaCalculator"));
const FoodDatabase = lazy(() => import("@features/meal-planner/pages/FoodDatabase"));
const MealPlanner = lazy(() => import("@features/meal-planner/pages/MealPlanner"));
const NotFound = lazy(() => import("@features/public/pages/NotFound"));
const Auth = lazy(() => import("@features/auth/pages/Auth"));
const ResetPassword = lazy(() => import("@features/auth/pages/ResetPassword"));
import AdminProtectedRoute from "@features/auth/components/AdminProtectedRoute";
const UserProfile = lazy(() => import("@features/public/pages/UserProfile"));
const PrivacyPolicy = lazy(() => import("@features/public/pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("@features/public/pages/RefundPolicy"));
const Contact = lazy(() => import("@features/public/pages/Contact"));
const FAQ = lazy(() => import("@features/public/pages/FAQ"));
const Testimonials = lazy(() => import("@features/public/pages/Testimonials"));
const Payment = lazy(() => import("@features/billing/pages/Payment"));
const PaymentCallback = lazy(() => import("@features/billing/pages/PaymentCallback"));
const SpecialistDashboard = lazy(() => import("@features/dashboard/pages/SpecialistDashboard"));
const PerformanceReports = lazy(() => import("@features/dashboard/pages/PerformanceReports"));
const RenewalPayment = lazy(() => import("@features/billing/pages/RenewalPayment"));
const UnlockClients = lazy(() => import("@features/billing/pages/UnlockClients"));
const Subscribe = lazy(() => import("@features/billing/pages/Subscribe"));
const FreeTrial = lazy(() => import("@features/billing/pages/FreeTrial"));
const About = lazy(() => import("@features/public/pages/About"));
const ClinicRegister = lazy(() => import("@features/clinic/pages/ClinicRegister"));
const ClinicPending = lazy(() => import("@features/clinic/pages/ClinicPending"));
const ClinicPayment = lazy(() => import("@features/clinic/pages/ClinicPayment"));
const ClinicPaymentCallback = lazy(() => import("@features/clinic/pages/ClinicPaymentCallback"));
const ClinicDashboard = lazy(() => import("@features/clinic/pages/ClinicDashboard"));
const OrgDashboard = lazy(() => import("@features/org/pages/OrgDashboard"));
const ClinicRenew = lazy(() => import("@features/clinic/pages/ClinicRenew"));
const ClinicMealDesignerAddon = lazy(() => import("@features/clinic/pages/ClinicMealDesignerAddon"));
const ClinicPublicPage = lazy(() => import("@features/clinic/pages/ClinicPublicPage"));
const ClinicCustomPage = lazy(() => import("@features/clinic/pages/ClinicCustomPage"));
const ClinicTelehealthPolicy = lazy(() => import("@features/clinic/pages/ClinicTelehealthPolicy"));
const ClinicCategoryPage = lazy(() => import("@features/clinic/pages/ClinicCategoryPage"));
const ClinicBooking = lazy(() => import("@features/clinic/pages/ClinicBooking"));
const ClientPortal = lazy(() => import("@features/clinic/pages/ClientPortal"));
const ClinicClientPaymentCallback = lazy(() => import("@features/clinic/pages/ClinicClientPaymentCallback"));
const AdminShell = lazy(() => import("@features/admin/pages/AdminShell"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const FeedbackRequestWrapper = () => {
  const { user, pendingRequest, showDialog, setShowDialog } = useFeedbackRequest();
  
  const currentPath = window.location.pathname;
  const isPublicClinicPage = currentPath.startsWith('/clinic/') && 
    !currentPath.includes('/dashboard') && 
    !currentPath.includes('/register') && 
    !currentPath.includes('/pending') && 
    !currentPath.includes('/payment');

  if (!user || !pendingRequest || isPublicClinicPage) return null;

  return (
    <FeedbackRequestDialog
      open={showDialog}
      onOpenChange={setShowDialog}
      user={user}
      requestId={pendingRequest.id}
    />
  );
};

// Static /clinic/* routes that are NOT the public clinic page (ClinicPublicPage
// is the only one that sets its own clinic-specific favicon via slug).
const CLINIC_STATIC_SEGMENTS = new Set([
  "start", "register", "pending", "payment", "payment-callback", "dashboard", "meal-designer-addon",
]);

const AppContentInner = () => {
  usePageVisitTracker();

  const location = useLocation();
  const slugMatch = location.pathname.match(/^\/clinic\/([^/]+)$/);
  const isClinicPublicPage = !!slugMatch && !CLINIC_STATIC_SEGMENTS.has(slugMatch[1]);
  // Skip the default favicon reset on the public clinic page — it sets its
  // own clinic-logo favicon, and since child effects run before this
  // ancestor's, this call would otherwise overwrite it back to the default.
  useDynamicFavicon(undefined, isClinicPublicPage);

  return null;
};

// Capture recovery hash early and redirect if needed
const RecoveryRedirect = () => {
  const hash = window.location.hash;
  const isRecovery = hash.includes("type=recovery") || hash.includes("access_token");
  if (isRecovery) {
    // Store in sessionStorage before Supabase clears the hash
    sessionStorage.setItem("dietitech_recovery", "true");
    if (window.location.pathname !== "/reset-password") {
      window.location.replace("/reset-password" + hash);
    }
  }
  return null;
};

const AppContent = () => {
  useActiveTheme();
  usePresenceTracker();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  return (
    <BrowserRouter>
      <RecoveryRedirect />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<StartClinic />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/meal-designer" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/calculator" element={<CalorieCalculator />} />
          <Route path="/dietitian-calculator" element={<DietitianCalculator />} />
          <Route path="/renal-calculator" element={<RenalCalculator />} />
          <Route path="/formula-calculator" element={<FormulaCalculator />} />
          <Route path="/food-database" element={<FoodDatabase />} />
          <Route path="/meal-planner" element={<MealPlanner />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/dashboard" element={<SpecialistDashboard />} />
          <Route path="/admin" element={<AdminProtectedRoute><AdminShell /></AdminProtectedRoute>} />
          <Route path="/dashboard/reports" element={<PerformanceReports />} />
          <Route path="/renewal" element={<RenewalPayment />} />
          <Route path="/unlock-clients" element={<UnlockClients />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/free-trial" element={<FreeTrial />} />
          <Route path="/about" element={<About />} />
          {/* Clinic Routes */}
          <Route path="/clinic/start" element={<Navigate to="/" replace />} />
          <Route path="/clinic/register" element={<ClinicRegister />} />
          <Route path="/clinic/pending" element={<ClinicPending />} />
          <Route path="/clinic/payment" element={<ClinicPayment />} />
          <Route path="/clinic/payment-callback" element={<ClinicPaymentCallback />} />
          <Route path="/clinic/dashboard" element={<ClinicDashboard />} />
          <Route path="/clinic/renew/:clinicId" element={<ClinicRenew />} />
          <Route path="/clinic/meal-designer-addon" element={<ClinicMealDesignerAddon />} />
          <Route path="/org-dashboard" element={<OrgDashboard />} />
          <Route path="/clinic/:slug" element={<ClinicPublicPage />} />
          <Route path="/clinic/:slug/category/:categoryId" element={<ClinicCategoryPage />} />
          <Route path="/clinic/:slug/telehealth-policy" element={<ClinicTelehealthPolicy />} />
          <Route path="/clinic/:slug/:pageType" element={<ClinicCustomPage />} />
          <Route path="/clinic/:slug/book/:productId" element={<ClinicBooking />} />
          <Route path="/clinic/:slug/payment-callback" element={<ClinicClientPaymentCallback />} />
          <Route path="/clinic/:slug/portal" element={<ClientPortal />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <AppContentInner />
      <FeedbackRequestWrapper />
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <PlatformLogoProvider>
          <AdminImpersonationProvider>
            <MaintenanceMode>
            <Toaster />
            <Sonner />
            <Sentry.ErrorBoundary fallback={({ error }) => {
              if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
                return (
                  <div className="min-h-screen flex items-center justify-center">
                    <p className="text-muted-foreground">جارٍ تحديث الصفحة...</p>
                  </div>
                );
              }
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <p className="text-muted-foreground">حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة.</p>
                </div>
              );
            }}>
              <AppContent />
            </Sentry.ErrorBoundary>
          </MaintenanceMode>
        </AdminImpersonationProvider>
      </PlatformLogoProvider>
    </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
